import moment from 'moment'
import { fetchCompany } from '@/api/parameters'
import { lineBreakdownSection } from './lineBreakdownPdf'

export async function generateDeliveryDashboardPdf(payload) {
  const pdfMake = require("pdfmake/build/pdfmake.js")
  const pdfFonts = require("pdfmake/build/vfs_fonts.js")
  pdfMake.vfs = pdfFonts.pdfMake.vfs

  // 1. Datos de la empresa
  let company = payload.company || {}
  try {
    if (!company.name && !company.fantasy) {
      company = await fetchCompany()
    }
  } catch (e) {
    company = {}
  }

  const companyName = company.fantasy || company.name || 'Runflow ERP'
  const companyRut = company.rut || ''
  const timezone = payload.timezone || company.current_time_zone || 'America/Santiago'

  const asOf = payload.asOf || moment().format('YYYY-MM-DD')
  const formattedAsOf = moment(asOf).format('DD/MM/YYYY')
  const lineName = payload.lineName || 'Todas las líneas'
  const now = moment()
  const emissionDate = now.format('DD/MM/YYYY HH:mm')

  const kpis = payload.kpis || {
    late: { count: 0, m2: 0, qty: 0 },
    today: { count: 0, m2: 0, qty: 0 },
    week: { count: 0, m2: 0, qty: 0 },
    later: { count: 0, m2: 0, qty: 0 },
    open: 0,
    prestage: 0,
    without_date: 0
  }

  const days = payload.days || []
  const customers = payload.customers || []
  const sellers = payload.sellers || []
  const orders = payload.orders || []

  const formatNumber = (num, decimals = 1) => {
    const val = Number(num || 0)
    return val.toLocaleString('es-CL', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    })
  }

  // 2. Tablas Top Clientes y Vendedores (Landscape: Top 6 para balancear altura)
  const customerRows = [
    [
      { text: '#', style: 'th', alignment: 'center', width: 14 },
      { text: 'CLIENTE', style: 'th', width: '*' },
      { text: 'ATRASADAS', style: 'th', alignment: 'center', width: 42 },
      { text: 'TOTAL OT', style: 'th', alignment: 'center', width: 34 },
      { text: 'VOLUMEN (M²)', style: 'th', alignment: 'right', width: 50 }
    ]
  ]

  const topCustomers = customers.slice(0, 7)
  if (topCustomers.length === 0) {
    customerRows.push([
      { text: 'Sin clientes con atrasos reportados', colSpan: 5, alignment: 'center', color: '#94A3B8', fontSize: 7, margin: [0, 6, 0, 6] },
      {}, {}, {}, {}
    ])
  } else {
    topCustomers.forEach((c, idx) => {
      customerRows.push([
        { text: String(idx + 1), alignment: 'center', fontSize: 7, bold: true, color: '#64748B' },
        { text: c.name || 'Sin cliente', fontSize: 7, bold: true, color: '#0F172A' },
        { text: String(c.late || 0), alignment: 'center', fontSize: 7, bold: true, color: c.late > 0 ? '#B91C1C' : '#64748B' },
        { text: String(c.count || 0), alignment: 'center', fontSize: 7, color: '#475569' },
        { text: `${formatNumber(c.m2)} m²`, alignment: 'right', fontSize: 7, bold: true, color: '#0F172A' }
      ])
    })
  }

  const sellerRows = [
    [
      { text: '#', style: 'th', alignment: 'center', width: 14 },
      { text: 'EJECUTIVO DE VENTAS', style: 'th', width: '*' },
      { text: 'ATRASADAS', style: 'th', alignment: 'center', width: 42 },
      { text: 'TOTAL OT', style: 'th', alignment: 'center', width: 34 },
      { text: 'VOLUMEN (M²)', style: 'th', alignment: 'right', width: 50 }
    ]
  ]

  const topSellers = sellers.slice(0, 7)
  if (topSellers.length === 0) {
    sellerRows.push([
      { text: 'Sin ejecutivos con datos en el período', colSpan: 5, alignment: 'center', color: '#94A3B8', fontSize: 7, margin: [0, 6, 0, 6] },
      {}, {}, {}, {}
    ])
  } else {
    topSellers.forEach((s, idx) => {
      sellerRows.push([
        { text: String(idx + 1), alignment: 'center', fontSize: 7, bold: true, color: '#64748B' },
        { text: s.name || 'Sin vendedor', fontSize: 7, bold: true, color: '#0F172A' },
        { text: String(s.late || 0), alignment: 'center', fontSize: 7, bold: true, color: s.late > 0 ? '#B91C1C' : '#64748B' },
        { text: String(s.count || 0), alignment: 'center', fontSize: 7, color: '#475569' },
        { text: `${formatNumber(s.m2)} m²`, alignment: 'right', fontSize: 7, bold: true, color: '#0F172A' }
      ])
    })
  }

  // 3. Matriz de Carga Próximos 14 Días
  const horizonDays = days.slice(0, 14)
  const dayRows = [
    [
      { text: 'FECHA', style: 'th', alignment: 'center', width: 46 },
      { text: 'DÍA', style: 'th', alignment: 'center', width: 28 },
      { text: 'ESTADO', style: 'th', alignment: 'center', width: 40 },
      { text: 'OTS', style: 'th', alignment: 'center', width: 26 },
      { text: 'VOLUMEN (M²)', style: 'th', alignment: 'right', width: 52 }
    ]
  ]

  horizonDays.forEach(d => {
    const isToday = d.is_today
    const isWeekend = d.is_weekend
    dayRows.push([
      { text: moment(d.date).format('DD/MM/YYYY'), alignment: 'center', fontSize: 6.5, bold: isToday, color: isToday ? '#0284C7' : '#334155' },
      { text: d.weekday ? d.weekday.toUpperCase() : '-', alignment: 'center', fontSize: 6.5, color: isWeekend ? '#94A3B8' : '#475569', bold: isToday },
      { text: isToday ? 'HOY' : (isWeekend ? 'Fin Sem.' : 'Hábil'), alignment: 'center', fontSize: 6, bold: isToday, color: isToday ? '#0284C7' : '#64748B' },
      { text: String(d.count || 0), alignment: 'center', fontSize: 6.5, bold: d.count > 0, color: d.count > 0 ? '#0F172A' : '#94A3B8' },
      { text: `${formatNumber(d.m2)} m²`, alignment: 'right', fontSize: 6.5, bold: d.m2 > 0, color: d.m2 > 0 ? '#0F172A' : '#94A3B8' }
    ])
  })

  // 4. Tabla Detallada de OTs Comprometidas (Página 2 en Landscape: 736 pt)
  const woRows = [
    [
      { text: 'N° OT', style: 'th', alignment: 'center', width: 48 },
      { text: 'CLIENTE / PROYECTO', style: 'th', width: '*' },
      { text: 'VENDEDOR', style: 'th', width: 105 },
      { text: 'F. COMPROMISO', style: 'th', alignment: 'center', width: 75 },
      { text: 'ESTADO', style: 'th', alignment: 'center', width: 65 },
      { text: 'VOL. (M²)', style: 'th', alignment: 'right', width: 60 },
      { text: 'PROD.', style: 'th', alignment: 'center', width: 45 },
      { text: 'ETAPA', style: 'th', alignment: 'center', width: 55 }
    ]
  ]

  if (orders.length === 0) {
    woRows.push([
      { text: 'No hay órdenes de trabajo comprometidas en el filtro seleccionado.', colSpan: 8, alignment: 'center', color: '#94A3B8', fontSize: 8, margin: [0, 10, 0, 10] },
      {}, {}, {}, {}, {}, {}, {}
    ])
  } else {
    orders.forEach((o, index) => {
      const isAlt = index % 2 === 1
      const bgColor = isAlt ? '#F8FAFC' : '#FFFFFF'

      let statusText = 'Normal'
      let statusColor = '#475569'
      if (o.status === 'late') {
        statusText = `Atraso ${Math.abs(o.days)}d`
        statusColor = '#B91C1C'
      } else if (o.status === 'today') {
        statusText = 'HOY'
        statusColor = '#C2410C'
      } else if (o.status === 'week') {
        statusText = `En ${o.days}d`
        statusColor = '#0F766E'
      } else {
        statusText = `En ${o.days}d`
        statusColor = '#334155'
      }

      woRows.push([
        { text: `OT-${o.id}`, alignment: 'center', fontSize: 7, bold: true, color: '#0284C7', fillColor: bgColor },
        {
          stack: [
            { text: o.customer || 'Sin Cliente', fontSize: 7, bold: true, color: '#0F172A' },
            { text: o.title || '', fontSize: 6, color: '#64748B' }
          ],
          fillColor: bgColor
        },
        { text: o.seller || 'Sin Vendedor', fontSize: 6.5, color: '#334155', fillColor: bgColor },
        { text: o.delivery_date ? moment(o.delivery_date).format('DD/MM/YYYY') : 'Sin Fecha', alignment: 'center', fontSize: 6.5, color: '#334155', fillColor: bgColor },
        { text: statusText, alignment: 'center', fontSize: 6.5, bold: true, color: statusColor, fillColor: bgColor },
        { text: `${formatNumber(o.m2)} m²`, alignment: 'right', fontSize: 7, bold: true, color: '#0F172A', fillColor: bgColor },
        { text: `${o.products_done || 0}/${o.products_total || 0}`, alignment: 'center', fontSize: 6.5, color: '#475569', fillColor: bgColor },
        { text: o.in_prestage ? 'Preprensa' : 'Planta', alignment: 'center', fontSize: 6.5, bold: o.in_prestage, color: o.in_prestage ? '#C2410C' : '#64748B', fillColor: bgColor }
      ])
    })
  }

  // 5. Document Definition (Landscape - Igual a Ventas, Producción y Facturación)
  const docDefinition = {
    pageSize: 'LETTER',
    pageOrientation: 'landscape',
    pageMargins: [28, 22, 28, 22],
    footer: function(currentPage, pageCount) {
      return {
        margin: [28, 0, 28, 0],
        columns: [
          { text: 'RUNFLOW ERP · DASHBOARD ENTREGAS', fontSize: 7, color: '#94A3B8', bold: true },
          { text: `Página ${currentPage} de ${pageCount}`, alignment: 'right', fontSize: 7, color: '#94A3B8', bold: true }
        ]
      }
    },
    content: [
      // ==========================================
      // PÁGINA 1: COCKPIT EJECUTIVO (ONE-PAGER LANDSCAPE)
      // ==========================================

      // 1. Header Banner Midnight Slate (#0F172A) con acento esmeralda
      {
        table: {
          widths: ['*'],
          body: [
            [
              {
                fillColor: '#0F172A',
                border: [false, false, false, false],
                margin: [16, 10, 16, 10],
                columns: [
                  {
                    width: '*',
                    stack: [
                      {
                        text: 'DASHBOARD ENTREGAS · REPORTE EJECUTIVO DE COMPROMISOS',
                        color: '#00A650',
                        fontSize: 8,
                        bold: true,
                        characterSpacing: 1.2,
                        margin: [0, 0, 0, 3]
                      },
                      {
                        text: companyName.toUpperCase(),
                        color: '#FFFFFF',
                        fontSize: 15,
                        bold: true,
                        margin: [0, 0, 0, 2]
                      },
                      {
                        text: `RUT: ${companyRut || 'N/A'}  ·  Supervisión de compromisos de despacho y órdenes abiertas  ·  Línea: ${lineName}`,
                        color: '#94A3B8',
                        fontSize: 8
                      }
                    ]
                  },
                  {
                    width: 220,
                    alignment: 'right',
                    stack: [
                      {
                        text: 'ESTADO AL DÍA',
                        alignment: 'right',
                        color: '#00A650',
                        fontSize: 7.5,
                        bold: true,
                        margin: [0, 0, 0, 2]
                      },
                      {
                        text: formattedAsOf,
                        alignment: 'right',
                        color: '#FFFFFF',
                        fontSize: 13,
                        bold: true,
                        margin: [0, 0, 0, 2]
                      },
                      {
                        text: `Emitido: ${emissionDate} hrs (${timezone})`,
                        alignment: 'right',
                        color: '#94A3B8',
                        fontSize: 7
                      }
                    ]
                  }
                ]
              }
            ]
          ]
        },
        layout: {
          hLineWidth: (i) => i === 1 ? 3 : 0,
          vLineWidth: () => 0,
          hLineColor: () => '#00A650',
          paddingLeft: () => 0,
          paddingRight: () => 0,
          paddingTop: () => 0,
          paddingBottom: () => 0
        },
        margin: [0, 0, 0, 10]
      },

      // 2. Row de 4 Hero KPIs Maestros (Landscape: 4 columnas balanceadas)
      {
        table: {
          widths: ['25%', '25%', '25%', '25%'],
          body: [
            [
              // Card 1: Atrasadas
              {
                fillColor: kpis.late.count > 0 ? '#FEF2F2' : '#F8FAFC',
                border: [false, false, false, false],
                margin: [10, 8, 10, 8],
                stack: [
                  { text: 'OT ATRASADAS', fontSize: 6.5, bold: true, color: '#B91C1C', characterSpacing: 0.5 },
                  { text: String(kpis.late.count || 0), fontSize: 16, bold: true, color: '#B91C1C', margin: [0, 2, 0, 2] },
                  { text: `${formatNumber(kpis.late.m2)} m² · ${kpis.late.qty || 0} un`, fontSize: 6.5, bold: true, color: '#7F1D1D' }
                ]
              },
              // Card 2: Hoy
              {
                fillColor: '#FFFBEB',
                border: [false, false, false, false],
                margin: [10, 8, 10, 8],
                stack: [
                  { text: 'ENTREGAS HOY', fontSize: 6.5, bold: true, color: '#B45309', characterSpacing: 0.5 },
                  { text: String(kpis.today.count || 0), fontSize: 16, bold: true, color: '#B45309', margin: [0, 2, 0, 2] },
                  { text: `${formatNumber(kpis.today.m2)} m² · ${kpis.today.qty || 0} un`, fontSize: 6.5, bold: true, color: '#92400E' }
                ]
              },
              // Card 3: Esta Semana
              {
                fillColor: '#F0FDFA',
                border: [false, false, false, false],
                margin: [10, 8, 10, 8],
                stack: [
                  { text: 'ESTA SEMANA', fontSize: 6.5, bold: true, color: '#0F766E', characterSpacing: 0.5 },
                  { text: String(kpis.week.count || 0), fontSize: 16, bold: true, color: '#0F766E', margin: [0, 2, 0, 2] },
                  { text: `${formatNumber(kpis.week.m2)} m² · ${kpis.week.qty || 0} un`, fontSize: 6.5, bold: true, color: '#115E59' }
                ]
              },
              // Card 4: Total OTs Abiertas
              {
                fillColor: '#F8FAFC',
                border: [false, false, false, false],
                margin: [10, 8, 10, 8],
                stack: [
                  { text: 'TOTAL OTS COMPROMETIDAS', fontSize: 6.5, bold: true, color: '#475569', characterSpacing: 0.5 },
                  { text: String(kpis.open || 0), fontSize: 16, bold: true, color: '#0F172A', margin: [0, 2, 0, 2] },
                  { text: `${kpis.prestage || 0} en preprensa · ${kpis.without_date || 0} sin fecha`, fontSize: 6.5, color: '#64748B' }
                ]
              }
            ]
          ]
        },
        layout: {
          hLineWidth: () => 1,
          vLineWidth: (i) => (i === 0 || i === 4) ? 1 : 0.5,
          hLineColor: () => '#E2E8F0',
          vLineColor: () => '#E2E8F0',
          paddingLeft: () => 0,
          paddingRight: () => 0,
          paddingTop: () => 0,
          paddingBottom: () => 0
        },
        margin: [0, 0, 0, 12]
      },

      ...lineBreakdownSection({
        title: 'COMPROMISOS POR LÍNEA DE NEGOCIO (TODAS LAS LÍNEAS)',
        rows: payload.lines || [],
        selectedName: lineName,
        columns: [
          { header: 'LÍNEA', value: (l) => l.name },
          { header: 'OTS', width: 60, align: 'center', value: (l) => String(l.count || 0) },
          { header: 'ATRASADAS', width: 70, align: 'center', value: (l) => String(l.late || 0) },
          { header: 'M²', width: 90, align: 'right', bold: true, value: (l) => formatNumber(l.m2) },
          { header: 'UNIDADES', width: 90, align: 'right', bold: true, value: (l) => formatNumber(l.qty, 0) }
        ]
      }),

      // 3. Bloque Inferior Landscape: 3 Columnas Proporcionales (736 pt utilizables)
      {
        columns: [
          // Columna 1: Carga de Entregas (14 Días)
          {
            width: 218,
            stack: [
              {
                text: 'CARGA DE ENTREGAS (14 DÍAS)',
                fontSize: 7.5,
                bold: true,
                color: '#0F172A',
                margin: [0, 0, 0, 4]
              },
              {
                table: {
                  headerRows: 1,
                  widths: [46, 28, 40, 26, 52],
                  body: dayRows
                },
                layout: {
                  hLineWidth: (i) => i === 1 ? 1 : 0.5,
                  vLineWidth: () => 0,
                  hLineColor: (i) => i === 1 ? '#0F172A' : '#E2E8F0',
                  paddingLeft: () => 2.5,
                  paddingRight: () => 2.5,
                  paddingTop: () => 2,
                  paddingBottom: () => 2
                }
              }
            ]
          },
          // Columna 2: Clientes con más atrasos / carga
          {
            width: 250,
            margin: [9, 0, 0, 0],
            stack: [
              {
                text: 'CLIENTES CON MAYOR ATRASO / CARGA',
                fontSize: 7.5,
                bold: true,
                color: '#0F172A',
                margin: [0, 0, 0, 4]
              },
              {
                table: {
                  headerRows: 1,
                  widths: [14, '*', 42, 34, 50],
                  body: customerRows
                },
                layout: {
                  hLineWidth: (i) => i === 1 ? 1 : 0.5,
                  vLineWidth: () => 0,
                  hLineColor: (i) => i === 1 ? '#0F172A' : '#E2E8F0',
                  paddingLeft: () => 3,
                  paddingRight: () => 3,
                  paddingTop: () => 3,
                  paddingBottom: () => 3
                }
              }
            ]
          },
          // Columna 3: Ejecutivos de ventas
          {
            width: 250,
            margin: [9, 0, 0, 0],
            stack: [
              {
                text: 'EJECUTIVOS DE VENTAS CON MÁS ATRASOS',
                fontSize: 7.5,
                bold: true,
                color: '#0F172A',
                margin: [0, 0, 0, 4]
              },
              {
                table: {
                  headerRows: 1,
                  widths: [14, '*', 42, 34, 50],
                  body: sellerRows
                },
                layout: {
                  hLineWidth: (i) => i === 1 ? 1 : 0.5,
                  vLineWidth: () => 0,
                  hLineColor: (i) => i === 1 ? '#0F172A' : '#E2E8F0',
                  paddingLeft: () => 3,
                  paddingRight: () => 3,
                  paddingTop: () => 3,
                  paddingBottom: () => 3
                }
              }
            ]
          }
        ],
        margin: [0, 0, 0, 10]
      },

      // ==========================================
      // PÁGINA 2: AUDITORÍA DETALLADA DE OTS COMPROMETIDAS (LANDSCAPE)
      // ==========================================
      {
        text: '',
        pageBreak: 'before'
      },
      {
        columns: [
          {
            stack: [
              { text: 'AUDITORÍA DETALLADA DE ÓRDENES COMPROMETIDAS', fontSize: 11, bold: true, color: '#0F172A' },
              { text: `Listado de órdenes de trabajo abiertas en seguimiento operativo · ${orders.length} OTs comprometidas`, fontSize: 7.5, color: '#64748B', margin: [0, 2, 0, 6] }
            ]
          },
          {
            text: `As of ${formattedAsOf}`,
            alignment: 'right',
            fontSize: 7.5,
            color: '#64748B',
            margin: [0, 4, 0, 0]
          }
        ]
      },
      {
        table: {
          headerRows: 1,
          dontBreakRows: true,
          widths: [48, '*', 105, 75, 65, 60, 45, 55],
          body: woRows
        },
        layout: {
          hLineWidth: (i) => i === 1 ? 1 : 0.5,
          vLineWidth: () => 0,
          hLineColor: (i) => i === 1 ? '#0F172A' : '#E2E8F0',
          paddingLeft: () => 4,
          paddingRight: () => 4,
          paddingTop: () => 3,
          paddingBottom: () => 3
        }
      }
    ],
    styles: {
      th: {
        fontSize: 6.5,
        bold: true,
        color: '#FFFFFF',
        fillColor: '#0F172A',
        margin: [0, 2, 0, 2]
      }
    },
    defaultStyle: {
      font: 'Roboto'
    }
  }

  pdfMake.createPdf(docDefinition).download(`Dashboard_Entregas_${moment().format('YYYYMMDD_HHmm')}.pdf`)
}
